import { Fragment, useState, useMemo } from 'react';
import { AppWindow, ChevronDown, ChevronRight, Database, ExternalLink, Settings, Square, SquaresUnite, TabletSmartphone } from 'lucide-react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import { FaAndroid, FaApple } from 'react-icons/fa';
import useTestResults from '../../hooks/useTestResults';
import SlidingWindow from '../SlidingWindow/SlidingWindow';
import { formatLocalDate } from '../../utils/dateFormat';
import './BrokenTestsTable.css';
import '../../styles/osPills.css';

const PRESET_TITLE = {
  today: 'for today',
  yesterday: 'for yesterday',
  '7d': 'for last 7 days',
  '14d': 'for last 14 days',
  '30d': 'for last 30 days',
};

const NO_SPEC_LABEL = 'No project';

// Columns whose group total is comparable to the per-test value, so group ordering can follow the active sort.
const AGGREGATED_COLUMNS = ['failureRate', 'passed', 'failed', 'skipped'];

// Quality Service leaves skipped runs out of the failure rate (failed / (passed + failed), 2 decimals).
// Group totals reuse that denominator so a group never disagrees with the rows it contains.
const aggregateResults = (items) => {
  const totals = items.reduce((acc, { results }) => ({
    passed: acc.passed + results.passed,
    failed: acc.failed + results.failed,
    skipped: acc.skipped + results.skipped,
  }), { passed: 0, failed: 0, skipped: 0 });

  const executed = totals.passed + totals.failed;
  const failureRate = executed > 0 ? Math.round((totals.failed / executed) * 10000) / 100 : 0;

  return { ...totals, failureRate };
};

// specDescription is reported as "Error Analysis / Crashes": the leading area is context, the last part is the spec.
const splitSpecDescription = (specDescription) => {
  const separator = ' / ';
  const index = (specDescription || '').lastIndexOf(separator);

  return index === -1
    ? { area: null, name: specDescription || NO_SPEC_LABEL }
    : { area: specDescription.slice(0, index), name: specDescription.slice(index + separator.length) };
};

// System E2E specs and test names lead with their Jira test case key
// ("QA-6940 [SDK] Data generation"). The key belongs in the Test ID column, so it is
// split off once here and the remaining label is what the name columns show.
const TEST_CASE_ID_PATTERN = /^([A-Z][A-Z0-9]*-\d+)\s+(.*)$/;

const splitTestCaseId = (label) => {
  const match = TEST_CASE_ID_PATTERN.exec((label || '').trim());

  return match
    ? { id: match[1], name: match[2] }
    : { id: null, name: label || '' };
};

// System E2E test names carry a bracketed tag ([SDK], [DT], [APP]) naming the surface under test.
const testNameTag = (testName) => {
  const match = /\[(SDK|DT|APP)\]/i.exec(testName || '');

  return match ? match[1].toUpperCase() : null;
};

// The tag also names the actual runner; the framework field is unreliable for this package
// (always null), so the icon prefers the tag and only falls back to item.framework without one.
const inferFrameworkFromTestName = (testName) => {
  const tag = testNameTag(testName);
  if (tag === 'SDK' || tag === 'DT') return 'pytest';
  if (tag === 'APP') return 'playwright';
  return null;
};

// Each tag reuses the icon its area carries in the left nav, so a group of sub-tests can be
// scanned by surface: Data collection / Mobile, Data processing, Application.
const TAG_ICONS = {
  SDK: TabletSmartphone,
  DT: Database,
  APP: AppWindow,
};

// System E2E runs the same spec once per mobile OS, reported through the package name
// ("@contentsquare/e2e-system-testing-android") and echoed in the test id prefix. Each OS gets
// its own group so a parent row never sums iOS and Android runs together.
const OS_PILLS = {
  android: { label: 'Android', Icon: FaAndroid, className: 'tm-os-pill tm-os-android' },
  ios: { label: 'iOS', Icon: FaApple, className: 'tm-os-pill tm-os-ios' },
};

const inferOs = ({ packageName, testId }) => {
  const source = `${packageName || ''} ${testId || ''}`.toLowerCase();
  if (/(^|[^a-z])android/.test(source)) return 'android';
  if (/(^|[^a-z])ios/.test(source)) return 'ios';
  return null;
};

// That icon is what names the surface on a row, so the tag is dropped from the label itself:
// "QA-6940 [SDK] Data generation" reads as "Data generation".
const testRowLabel = (testName) =>
  splitTestCaseId(testName).name.replace(/\s*\[(SDK|DT|APP)\]\s*/i, ' ').trim();

const rowColors = (rate) => {
  const failureRate = parseFloat(rate);
  if (failureRate > 5) return { background: '#ffebee', hover: '#ffcdd2' };
  if (failureRate > 0) return { background: '#fff3e0', hover: '#ffe0b2' };
  return { background: 'transparent', hover: '#f5f5f5' };
};

// Group rows summarize every child test at a glance, so they use a plain pass/fail
// signal (any failure at all) rather than the tiered failure-rate bands per-test rows use.
const groupRowColors = (failed) => failed > 0
  ? { background: '#ffebee', hover: '#ffcdd2' }
  : { background: '#ffffff', hover: '#f5f5f5' };

const TestResultsTable = ({ selectedDate, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, selectedIsCsqXp = null, csqXpFalseLabel = 'OneApp', selectedStartDate = null, selectedEndDate = null, datePreset = null, groupBySpecDescription = false }) => {
  const { data, loading, error } = useTestResults(selectedDate, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, selectedIsCsqXp, selectedStartDate, selectedEndDate);

  const startKey = selectedStartDate ? formatLocalDate(selectedStartDate) : null;
  const endKey = selectedEndDate ? formatLocalDate(selectedEndDate) : null;
  const titleSuffix = datePreset && PRESET_TITLE[datePreset]
    ? PRESET_TITLE[datePreset]
    : startKey && endKey && startKey !== endKey
      ? `from ${startKey} to ${endKey}`
      : `for ${(endKey || formatLocalDate(selectedDate))}`;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('failureRate');
  const [expandedGroups, setExpandedGroups] = useState({});

  const showUnitColumn = selectedFramework === 'pytest';
  // Grouping already states the project on the group row, so the per-test column would only repeat it.
  const showProjectColumn = selectedPackage === '@contentsquare/e2e-system-testing' && !groupBySpecDescription;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal, bVal;

    if (orderBy === 'failureRate') {
      aVal = parseFloat(a.results.failureRate);
      bVal = parseFloat(b.results.failureRate);
    } else if (['passed', 'failed', 'skipped'].includes(orderBy)) {
      aVal = a.results[orderBy];
      bVal = b.results[orderBy];
    } else {
      aVal = a[orderBy];
      bVal = b[orderBy];
    }

    if (bVal < aVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  };

  // Filter data based on selected framework
  // Handle the case where wdio tests have null/undefined framework values
  const filteredData = useMemo(() => {
    return data && selectedFramework && selectedFramework !== 'All Frameworks'
      ? data.filter(item => {
        if (selectedFramework === 'Playwright') {
          return item.framework === 'playwright';
        } else if (selectedFramework === 'WDIO') {
          // Treat null, undefined, empty string, or explicitly 'wdio' as wdio
          return !item.framework || item.framework === 'wdio' || item.framework === '';
        } else if (selectedFramework === 'pytest') {
          // Treat null, undefined, empty string, or explicitly 'wdio' as wdio
          return !item.framework || item.framework === 'pytest';
        }
        return false;
      })
      : data || [];
  }, [data, selectedFramework]);

  const sortedData = useMemo(() => {
    if (!filteredData) return [];

    const getComparator = (order, orderBy) => {
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    };

    return [...filteredData].sort(getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);

  const groupedData = useMemo(() => {
    if (!groupBySpecDescription) return [];

    const groups = new Map();
    sortedData.forEach((item) => {
      const spec = item.specDescription || NO_SPEC_LABEL;
      const os = inferOs(item);
      const key = os ? `${spec} / ${OS_PILLS[os].label}` : spec;
      if (!groups.has(key)) groups.set(key, { spec, os, items: [] });
      groups.get(key).items.push(item);
    });

    // Sub-tests read as a list of names, so they stay alphabetical by the name their row shows
    // (test case key stripped off) whatever column the group rows themselves are ordered by.
    const byTestName = (a, b) => testRowLabel(a.testName).localeCompare(testRowLabel(b.testName));

    const withTotals = [...groups.entries()].map(([key, { spec, os, items }]) => ({
      key,
      spec,
      os,
      items: [...items].sort(byTestName),
      results: aggregateResults(items),
      // The test case key lives on the test names ("QA-6940 [SDK] Data generation"), so the
      // group row reports the keys its children carry. Normally that is a single key.
      testCaseIds: [...new Set(items.map(item => splitTestCaseId(item.testName).id).filter(Boolean))],
    }));

    const direction = order === 'desc' ? 1 : -1;

    return withTotals.sort((a, b) => {
      if (orderBy === 'specDescription') return a.key.localeCompare(b.key) * -direction;
      // A column with no group-level total (unit) leaves the most broken project first.
      if (!AGGREGATED_COLUMNS.includes(orderBy)) return b.results.failureRate - a.results.failureRate;
      return (b.results[orderBy] - a.results[orderBy]) * direction;
    });
  }, [groupBySpecDescription, sortedData, order, orderBy]);

  // Helper function to check if testId is a hash pattern and replace it
  const formatTestId = (testId) => {
    // Check if testId matches hash pattern (40 character hexadecimal string)
    const hashPattern = /^[a-f0-9]{40}$/i;
    if (hashPattern.test(testId)) {
      return <span style={{ color: 'red' }}>No Test Case ID</span>;
    }
    return testId;
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedItem(null);
  };

  // Groups that contain a failure open by default, so the broken tests stay visible without a click.
  const isGroupExpanded = (group) => expandedGroups[group.key] ?? group.results.failed > 0;

  const toggleGroup = (group) => {
    const expanded = isGroupExpanded(group);
    setExpandedGroups(prev => ({ ...prev, [group.key]: !expanded }));
  };

  const renderTestRow = (item, key, { nested = false } = {}) => {
    const colors = rowColors(item.results.failureRate);
    const framework = inferFrameworkFromTestName(item.testName) || item.framework;
    const tag = testNameTag(item.testName);
    const TagIcon = TAG_ICONS[tag];
    // The icon and the name it belongs to share one hover target, so the tooltip wraps both.
    const testNameLabel = (
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {TagIcon && <TagIcon size={16} style={{ flexShrink: 0 }} />}
        {testRowLabel(item.testName)}
      </span>
    );

    return (
      <TableRow
        key={key}
        sx={{
          backgroundColor: colors.background,
          '&:hover': { backgroundColor: colors.hover },
          cursor: 'pointer',
          '&:last-child td, &:last-child th': { border: 0 }
        }}
        onClick={() => handleRowClick(item)}
      >
        <TableCell sx={nested ? { pl: 4 } : undefined}>
          <ExternalLink size={16} className="broken-tests-expand-icon" />
        </TableCell>
        <TableCell>{formatTestId(item.testId)}</TableCell>
        <TableCell>
          {tag
            ? <Tooltip title={`${tag} test`} describeChild>{testNameLabel}</Tooltip>
            : testNameLabel}
        </TableCell>
        {showUnitColumn && (
          <TableCell>{item.unit}</TableCell>
        )}
        <TableCell align="center">{item.results.passed}</TableCell>
        <TableCell align="center">{item.results.failed}</TableCell>
        <TableCell align="center">{item.results.skipped}</TableCell>
        <TableCell align="center">{item.results.failureRate}</TableCell>
        <TableCell>
          {framework === 'playwright' ? (
            <img
              src={require('../../assets/playwright-logo.png')}
              alt="Playwright"
              style={{ height: '20px', width: 'auto' }}
            />
          ) : framework === 'pytest' ? (
            <img
              src={require('../../assets/python-logo.png')}
              alt="pytest"
              style={{ height: '20px', width: 'auto' }}
            />
          ) : (
            <img
              src={require('../../assets/wdio-logo.png')}
              alt={framework}
              style={{ height: '20px', width: 'auto' }}
            />
          )}
        </TableCell>
        <TableCell>{item.packageName}</TableCell>
        {showProjectColumn && (
          <TableCell>{item.specDescription}</TableCell>
        )}
        <TableCell>
          {item.isCsqXp === true
            ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><SquaresUnite size={16} />CSQ XP</span>
            : <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Square size={16} />{csqXpFalseLabel}</span>
          }
        </TableCell>
      </TableRow>
    );
  };

  const renderGroupRow = (group) => {
    const expanded = isGroupExpanded(group);
    const { area, name: specName } = splitSpecDescription(group.spec);
    const { id: labelId, name } = splitTestCaseId(specName);
    const [testCaseId] = group.testCaseIds.length > 0 ? group.testCaseIds : [labelId];
    const subTestCount = group.items.length;
    const colors = groupRowColors(group.results.failed);
    // The Test ID column now carries the group's test case key, leaving the label to span
    // Test Name (plus Unit when it is shown).
    const labelColSpan = showUnitColumn ? 2 : 1;
    // Framework, Package Name and Project say nothing at group level; Platform carries the OS.
    const trailingColSpan = showProjectColumn ? 3 : 2;
    const osPill = OS_PILLS[group.os];

    return (
      <TableRow
        sx={{
          backgroundColor: colors.background,
          '&:hover': { backgroundColor: colors.hover },
          cursor: 'pointer',
          '& td': { fontWeight: 600, borderTop: '2px solid #bdbdbd', borderBottom: '1px solid #ddd' }
        }}
        onClick={() => toggleGroup(group)}
      >
        <TableCell>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </TableCell>
        <TableCell>
          {/* The gear is System E2E's own left-nav icon, marking the test case the sub-tests belong to. */}
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {testCaseId && <Settings size={16} style={{ flexShrink: 0 }} />}
            {testCaseId}
            <span style={{ color: '#666', fontWeight: 400 }}>
              ({subTestCount} {subTestCount === 1 ? 'sub-test' : 'sub-tests'})
            </span>
          </span>
        </TableCell>
        <TableCell colSpan={labelColSpan}>
          {area && <span style={{ color: '#666', fontWeight: 400 }}>{area} / </span>}
          {name}
        </TableCell>
        <TableCell align="center">{group.results.passed}</TableCell>
        <TableCell align="center">{group.results.failed}</TableCell>
        <TableCell align="center">{group.results.skipped}</TableCell>
        <TableCell align="center">{group.results.failureRate}</TableCell>
        <TableCell colSpan={trailingColSpan} />
        <TableCell>
          {osPill && (
            <span className={osPill.className}>
              <osPill.Icon size={11} />
              {osPill.label}
            </span>
          )}
        </TableCell>
      </TableRow>
    );
  };

  if (loading) return (
    <div className="broken-tests-section">
      <h1 className="broken-tests-title">Most broken tests {titleSuffix}</h1>
      <div className="broken-tests-container">
        <div className="broken-tests-table-container flashing loading" style={{ minHeight: '200px' }}></div>
      </div>
    </div>
  );

  if (error) return (
    <div className="broken-tests-section">
      <h1 className="broken-tests-title">Most broken tests {titleSuffix}</h1>
      <div className="broken-tests-container">
        <div className="broken-tests-table-container flashing error" style={{ minHeight: '200px' }}></div>
      </div>
    </div>
  );

  if (data && data.length === 0) return (
    <div className="broken-tests-section">
      <h1 className="broken-tests-title">Most broken tests {titleSuffix}</h1>
      <div className="broken-tests-container">
        <div className="broken-tests-table-container flashing no-data" style={{ minHeight: '200px' }}></div>
      </div>
    </div>
  );

  if (data && data.length > 0 && filteredData.length === 0) return (
    <div className="broken-tests-section">
      <h1 className="broken-tests-title">Most broken tests {titleSuffix}</h1>
      <div className="broken-tests-container">
        <div className="broken-tests-table-container flashing no-data" style={{ minHeight: '200px' }}></div>
      </div>
    </div>
  );

  return (
    <div className="broken-tests-root">
      <div className="broken-tests-section">
        <h1 className="broken-tests-title">Most broken tests {titleSuffix}</h1>
        <div className="broken-tests-container">
          <div className="broken-tests-table-container">
            {/* height: 100% rather than a hardcoded calc(): the section above sizes itself from
                what the filters and cards leave over, so the table scrolls internally at
                whatever height the page actually has, and the sticky header works. */}
            <TableContainer component={Paper} sx={{ height: '100%' }}>
              <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="broken tests table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}></TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '12%' }}>Test ID</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: groupBySpecDescription ? '28%' : undefined }}>Test Name</TableCell>
                    {showUnitColumn && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={orderBy === 'unit'}
                          direction={orderBy === 'unit' ? order : 'asc'}
                          onClick={() => handleRequestSort('unit')}
                        >
                          Unit
                          {orderBy === 'unit' ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'passed'}
                        direction={orderBy === 'passed' ? order : 'asc'}
                        onClick={() => handleRequestSort('passed')}
                      >
                        Passed
                        {orderBy === 'passed' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'failed'}
                        direction={orderBy === 'failed' ? order : 'asc'}
                        onClick={() => handleRequestSort('failed')}
                      >
                        Failed
                        {orderBy === 'failed' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'skipped'}
                        direction={orderBy === 'skipped' ? order : 'asc'}
                        onClick={() => handleRequestSort('skipped')}
                      >
                        Skipped
                        {orderBy === 'skipped' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '10%' }}>
                      <TableSortLabel
                        active={orderBy === 'failureRate'}
                        direction={orderBy === 'failureRate' ? order : 'asc'}
                        onClick={() => handleRequestSort('failureRate')}
                      >
                        Failure Rate (%)
                        {orderBy === 'failureRate' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Framework</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      {/* Group rows carry no package name, so there is nothing to order them by. */}
                      {groupBySpecDescription ? 'Package Name' : (
                        <TableSortLabel
                            active={orderBy === 'packageName'}
                            direction={orderBy === 'packageName' ? order : 'asc'}
                            onClick={() => handleRequestSort('packageName')}
                          >
                            Package Name
                            {orderBy === 'packageName' ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      )}
                    </TableCell>
                    {showProjectColumn && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableSortLabel
                            active={orderBy === 'specDescription'}
                            direction={orderBy === 'specDescription' ? order : 'asc'}
                            onClick={() => handleRequestSort('specDescription')}
                          >
                            Project
                            {orderBy === 'specDescription' ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Platform</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupBySpecDescription
                    ? groupedData.map((group) => (
                      <Fragment key={group.key}>
                        {renderGroupRow(group)}
                        {isGroupExpanded(group) && group.items.map((item, index) => renderTestRow(item, `${group.key}-${index}`, { nested: true }))}
                      </Fragment>
                    ))
                    : sortedData.map((item, index) => renderTestRow(item, index))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
      <SlidingWindow isOpen={isOpen} onClose={handleClose} content={<div>{JSON.stringify(selectedItem, null, 2)}</div>} />
    </div>
  );
};

export default TestResultsTable;
